import EventEmitter from 'eventemitter3';
import { ScrollAnimation, easeInOutCubic } from './ScrollAnimation';

export type ScrollDirection = 'up' | 'down';
export type ScrollMode = 'panelmove' | 'dismissmove' | 'panemove';
export type ScrollHandler = (direction: ScrollDirection, mode: ScrollMode) => void;

export class VerticalScrollerCore extends EventEmitter {
  // Constants for thresholds and durations
  private readonly SCROLL_THRESHOLD = 10;
  private readonly TAP_THRESHOLD = 50; // ms to be considered a tap
  private readonly RELEASE_THRESHOLD = 0.05; // 5% of the pane height
  private readonly ANIMATION_DURATION = 200;

  // Touch state variables
  private touchStartY: number | null = null;
  private lastTouchY: number | null = null;
  private touchStartTime: number | null = null;
  private currentY: number = 0;

  // Scroll state variables
  private scrollMode: ScrollMode | null = null;
  private scrollDirection: ScrollDirection | null = null;
  private currentAnimation: ScrollAnimation | null = null;

  // Element dimensions
  private paneHeight: number;
  
  // Track if component is active for window keyboard events
  private isActive: boolean = false;

  constructor(
    private element: HTMLElement,
    private focusedId: string
  ) {
    super();
    this.paneHeight = element.clientHeight;
    this.bindEvents();
    // Note: Initial positioning is now handled by the React component
    // to prevent unwanted resets during re-renders
  }

  private bindEvents(): void {
    this.element.addEventListener('touchstart', this.handleTouchStart);
    this.element.addEventListener('touchmove', this.handleTouchMove);
    this.element.addEventListener('touchend', this.handleTouchEnd);

    // Desktop support
    this.element.addEventListener('wheel', this.handleWheel, { passive: false });
    
    // Use window-level keyboard events instead of element-level
    window.addEventListener('keydown', this.handleKeyDown);

    // Still set tabindex for accessibility but don't require focus for keyboard nav
    if (!this.element.hasAttribute('tabindex')) {
      this.element.setAttribute('tabindex', '0');
    }
  }

  public setActive(active: boolean): void {
    this.isActive = active;
  }

  public destroy(): void {
    this.element.removeEventListener('touchstart', this.handleTouchStart);
    this.element.removeEventListener('touchmove', this.handleTouchMove);
    this.element.removeEventListener('touchend', this.handleTouchEnd);
    this.element.removeEventListener('wheel', this.handleWheel);
    
    // Remove window-level keyboard listener
    window.removeEventListener('keydown', this.handleKeyDown);
    
    this.isActive = false;

    if (this.currentAnimation) {
      this.currentAnimation.cancel();
      this.currentAnimation = null;
    }

    this.removeAllListeners();
  }

  private handleTouchStart = (e: TouchEvent): void => {
    // Initialize touch start variables
    this.touchStartY = e.touches[0]?.clientY ?? 0;
    this.lastTouchY = this.touchStartY;
    this.touchStartTime = Date.now();
    this.currentY = 0;
    this.scrollMode = null;
    this.scrollDirection = null;

    // Cancel any ongoing animation
    if (this.currentAnimation) {
      this.currentAnimation.cancel();
      this.currentAnimation = null;
    }
  };

  private handleTouchMove = (e: TouchEvent): void => {
    if (this.touchStartY === null || this.lastTouchY === null) return;

    const currentY = e.touches[0]?.clientY ?? 0;
    const deltaY = this.lastTouchY - currentY;

    // Determine scroll direction if not already set
    if (!this.scrollDirection && Math.abs(deltaY) > this.SCROLL_THRESHOLD) {
      this.scrollDirection = deltaY > 0 ? 'up' : 'down';
    }

    // Determine scroll mode if not already set
    if (!this.scrollMode) {
      // Here you might implement logic to determine between 'panelmove' and 'dismissmove'
      this.scrollMode = 'panemove';
    }

    // Update current position
    this.currentY += deltaY;

    // Apply the movement
    if (this.scrollMode === 'panemove') {
      this.element.scrollTop += deltaY;
    } else if (this.scrollMode === 'panelmove') {
      // Implement panel move logic here
    } else if (this.scrollMode === 'dismissmove') {
      // Implement dismiss move logic here
    }

    // Update last touch position
    this.lastTouchY = currentY;
  };

  private handleTouchEnd = (): void => {
    if (this.touchStartY === null || this.lastTouchY === null || this.touchStartTime === null)
      return;

    const touchEndTime = Date.now();
    const touchDuration = touchEndTime - this.touchStartTime;

    // Calculate total movement
    const totalDeltaY = this.touchStartY - this.lastTouchY;

    // Determine if it was a tap or a move
    if (Math.abs(totalDeltaY) < this.SCROLL_THRESHOLD && touchDuration < this.TAP_THRESHOLD) {
      // Handle tap
      this.handleTap();
    } else {
      // Handle move end based on mode
      switch (this.scrollMode) {
        case 'panemove':
          this.handlePaneMoveEnd();
          break;
        case 'panelmove':
          this.handlePanelMoveEnd();
          break;
        case 'dismissmove':
          this.handleDismissMoveEnd();
          break;
      }
    }

    this.resetTouchState();
  };

  private handleTap(): void {
    this.emit('tap');
  }

  private handlePaneMoveEnd(): void {
    if (!this.scrollDirection) {
      this.setPane(this.focusedId);
      return;
    }

    const percentMoved = Math.abs(this.currentY) / this.paneHeight;

    if (percentMoved > this.RELEASE_THRESHOLD) {
      // Move to next/previous pane
      const currentPaneElement = this.element.querySelector(
        `[data-vertical-scroller-id="${this.focusedId}"]`
      ) as HTMLElement;
      if (currentPaneElement) {
        const currentIndex = Array.from(this.element.children).indexOf(currentPaneElement);
        const newIndex = this.scrollDirection === 'up' ? currentIndex + 1 : currentIndex - 1;
        const newPaneElement = this.element.children[newIndex] as HTMLElement;
        if (newPaneElement) {
          const newId = newPaneElement.getAttribute('data-vertical-scroller-id');
          if (newId) {
            this.setPane(newId);
            return;
          }
        }
      }
    }
    this.setPane(this.focusedId);
  }

  private handlePanelMoveEnd(): void {
    // Implement panel move end logic here
  }

  private handleDismissMoveEnd(): void {
    // Implement dismiss move logic here
  }

  // Note: This method is reserved for future smooth scrolling features
  // private animateToPosition(targetPosition: number): void {
  //     if (this.currentAnimation) {
  //         this.currentAnimation.cancel();
  //     }

  //     this.currentAnimation = new ScrollAnimation({
  //         element: this.element,
  //         targetPosition,
  //         duration: this.ANIMATION_DURATION,
  //         easing: easeInOutCubic,
  //         onComplete: () => {
  //             this.currentAnimation = null;
  //         },
  //         onCancel: () => {
  //             this.currentAnimation = null;
  //         },
  //     });
  // }

  private resetTouchState(): void {
    this.touchStartY = null;
    this.lastTouchY = null;
    this.touchStartTime = null;
    this.currentY = 0;
    this.scrollMode = null;
    this.scrollDirection = null;
  }

  public setPane(id: string, instant: boolean = false): void {
    const targetElement = this.element.querySelector(
      `[data-vertical-scroller-id="${id}"]`
    ) as HTMLElement;

    if (!targetElement) {
      console.error(`No element found with data-vertical-scroller-id="${id}"`);
      return;
    }

    if (instant) {
      this.element.scrollTop = targetElement.offsetTop;
      this.emit('set', id);
    } else {
      if (this.currentAnimation) {
        this.currentAnimation.cancel();
      }

      this.currentAnimation = new ScrollAnimation({
        element: this.element,
        targetPosition: targetElement.offsetTop,
        duration: this.ANIMATION_DURATION,
        easing: easeInOutCubic,
        onComplete: () => {
          this.emit('set', id);
          this.currentAnimation = null;
        },
        onCancel: () => {
          this.currentAnimation = null;
        },
      });
    }

    this.focusedId = id;
  }

  // Desktop support methods
  private handleWheel = (e: WheelEvent): void => {
    e.preventDefault();

    // Determine direction based on wheel delta
    const direction = e.deltaY > 0 ? 'down' : 'up';

    // Get current pane
    const currentPaneElement = this.element.querySelector(
      `[data-vertical-scroller-id="${this.focusedId}"]`
    ) as HTMLElement;
    if (!currentPaneElement) return;

    const currentIndex = Array.from(this.element.children).indexOf(currentPaneElement);
    const newIndex = direction === 'down' ? currentIndex + 1 : currentIndex - 1;

    // Check bounds
    if (newIndex < 0 || newIndex >= this.element.children.length) return;

    const newPaneElement = this.element.children[newIndex] as HTMLElement;
    const newId = newPaneElement.getAttribute('data-vertical-scroller-id');

    if (newId && newId !== this.focusedId) {
      this.setPane(newId);
    }
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    // Only handle keyboard events when this component is active
    if (!this.isActive) return;

    let direction: 'up' | 'down' | null = null;

    switch (e.key) {
      case 'ArrowUp':
      case 'PageUp':
        direction = 'up';
        break;
      case 'ArrowDown':
      case 'PageDown':
      case ' ': // Spacebar
        direction = 'down';
        break;
      default:
        return;
    }

    e.preventDefault();

    // Get current pane
    const currentPaneElement = this.element.querySelector(
      `[data-vertical-scroller-id="${this.focusedId}"]`
    ) as HTMLElement;
    if (!currentPaneElement) return;

    const currentIndex = Array.from(this.element.children).indexOf(currentPaneElement);
    const newIndex = direction === 'down' ? currentIndex + 1 : currentIndex - 1;

    // Check bounds
    if (newIndex < 0 || newIndex >= this.element.children.length) return;

    const newPaneElement = this.element.children[newIndex] as HTMLElement;
    const newId = newPaneElement.getAttribute('data-vertical-scroller-id');

    if (newId) {
      this.setPane(newId);
    }
  };
}
